import { get, writable } from 'svelte/store';

import { blockSize, fetchBlock } from '$lib/api/selection';
import { fetchMemberRows } from '$lib/api/memberrows';
import { env } from '$lib/config/environment';
import { logger } from '$lib';
import { mixedResults } from '$lib/stores/mixedResults';
import { queryResult } from '$lib/stores/selection';
import type { CompanyWithContacts } from '$lib/types/api';

/**
 * Filling in the rows of a selection, a block at a time.
 *
 * The search returns an index: every company the customer selected, with what
 * the list is sorted by and the identifiers an order is built from, but none
 * of what a row displays. This asks for the rest as the customer scrolls into
 * it, and writes it into the companies the rest of the app already holds — so
 * nothing downstream has to know that a row arrived late.
 */

/** Companies whose block is in flight, so it is asked for once. */
const inFlight = new Set<string>();

/** How many blocks are outstanding, for the list to show it is still filling. */
export const hydrating = writable(0);

/**
 * Ensure the rows for these companies have been fetched.
 *
 * Safe to call on every scroll: companies already fetched, and those whose
 * block is already in flight, are skipped, so the work is proportional to what
 * is newly visible rather than to how often this is called.
 */
export async function hydrate(companyIds: string[]): Promise<void> {
  const held = get(mixedResults).companies;
  const byID = new Map(held.map((c) => [c.id, c]));

  const wanted = companyIds.filter((id) => {
    if (inFlight.has(id)) return false;
    const company = byID.get(id);
    return company !== undefined && company.hydrated !== true;
  });

  if (wanted.length === 0) return;

  const blocks: string[][] = [];
  for (let at = 0; at < wanted.length; at += blockSize) {
    blocks.push(wanted.slice(at, at + blockSize));
  }

  await Promise.all(blocks.map(fetchOne));
}

async function fetchOne(block: string[]): Promise<void> {
  block.forEach((id) => inFlight.add(id));
  hydrating.update((n) => n + 1);

  try {
    // Which shape the rows come in follows the *build*, not the session.
    //
    // The member app's list is counts and indicators from /deep-iq/rows; the
    // public app's is the masked preview. Asking the session instead was the
    // bug this replaced: a token lapsing between one block and the next put
    // two different row shapes in one list, and the components drawing them
    // disagreed about which fields exist. The member build has no public
    // fallback to reach for — a lapsed session goes to the login screen.
    const rows = env.mode === 'member' ? await fetchMemberRows(block) : await fetchBlock(block);
    const byID = new Map(rows.map((row) => [row.id, row]));

    // The row wins on everything it carries, except the score: that comes from
    // the search that produced the index and a block fetch does not rank, so
    // taking its quality would overwrite a real similarity with "unscored".
    const merge = (company: CompanyWithContacts): CompanyWithContacts => {
      const row = byID.get(company.id);
      if (!row) return company;
      return { ...company, ...row, quality: company.quality };
    };

    mixedResults.update((state) => ({ ...state, companies: state.companies.map(merge) }));

    // Both stores, because `queryResult` is what the results screen resets
    // `mixedResults` from when a search lands. Filling only the latter meant a
    // reset put the skeletons back, and everything fetched was thrown away.
    queryResult.update((companies) => companies.map(merge));
  } catch (error) {
    // A block that fails is not fatal: the rows stay skeletons and the next
    // scroll past them tries again. Nothing the customer has bought depends on
    // it, because the order is built from the index.
    logger.warn('hydration: a block of rows failed', error);
  } finally {
    block.forEach((id) => inFlight.delete(id));
    hydrating.update((n) => Math.max(0, n - 1));
  }
}

/** Forget what is in flight, for a new search. */
export function resetHydration(): void {
  inFlight.clear();
  hydrating.set(0);
}
